import React, { useState } from 'react';
import { createUser, signInUser, getUserProfile, validateInvitation, markInvitationAsUsed, resetPassword } from '../services/firebase';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'SuperAdmin';
  createdAt: string;
  isSuperAdmin?: boolean;
}

interface LoginProps {
  onLogin: (user: User) => void;
}

// Function to translate Firebase errors into user-friendly messages
const getFriendlyErrorMessage = (errorMessage: string): string => {
  if (
    errorMessage.includes('auth/wrong-password') ||
    errorMessage.includes('auth/invalid-credential') ||
    errorMessage.includes('auth/invalid-login-credentials')
  ) {
    return 'Incorrect password. Please try again.';
  }
  if (errorMessage.includes('auth/user-not-found')) {
    return 'No account found with this email address. Please check your email or create a new account.';
  }
  if (errorMessage.includes('auth/invalid-email')) {
    return 'Please enter a valid email address.';
  }
  if (errorMessage.includes('auth/email-already-in-use')) {
    return 'An account with this email already exists. Please sign in instead.';
  }
  if (errorMessage.includes('auth/weak-password')) {
    return 'Password is too weak. Please choose a password with at least 6 characters.';
  }
  if (errorMessage.includes('auth/too-many-requests')) {
    return 'Too many failed login attempts. Please try again later.';
  }
  if (errorMessage.includes('auth/network-request-failed')) {
    return 'Network error. Please check your internet connection and try again.';
  }
  if (errorMessage.includes('auth/user-disabled')) {
    return 'This account has been disabled. Please contact support.';
  }
  if (errorMessage.includes('auth/operation-not-allowed')) {
    return 'This operation is not allowed. Please contact support.';
  }
  
  // Default fallback for unknown errors
  return 'An error occurred. Please try again.';
};

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [invitationCode, setInvitationCode] = useState('');
  const [invitationValid, setInvitationValid] = useState(false);
  const [validatingInvitation, setValidatingInvitation] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Validate invitation code
  const validateInvitationCode = async (code: string) => {
    if (!code.trim()) {
      setInvitationValid(false);
      return;
    }

    setValidatingInvitation(true);
    try {
      const result = await validateInvitation(code);
      setInvitationValid(result.valid);
      if (!result.valid) {
        setError(result.message || 'Invalid invitation code');
      } else {
        setError('');
      }
    } catch (error: any) {
      setInvitationValid(false);
      setError('Error validating invitation code');
    } finally {
      setValidatingInvitation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isRegistering) {
        // Check password confirmation
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setIsLoading(false);
          return;
        }

        // Check invitation code
        if (!invitationCode.trim()) {
          setError('Invitation code is required');
          setIsLoading(false);
          return;
        }

        if (!invitationValid) {
          setError('Please enter a valid invitation code');
          setIsLoading(false);
          return;
        }
        
        // Create new user with Firebase
        const firebaseUser = await createUser(email, password, name);
        
        // Mark invitation as used
        await markInvitationAsUsed(invitationCode, email);
        
        const userProfile = await getUserProfile(firebaseUser.uid);
        
        if (userProfile) {
          const createdAtIso = (userProfile?.createdAt && typeof (userProfile.createdAt as any).toDate === 'function')
            ? (userProfile.createdAt as any).toDate().toISOString()
            : (typeof userProfile.createdAt === 'string' ? userProfile.createdAt : new Date().toISOString());
          const user: User = {
            id: firebaseUser.uid,
            email: firebaseUser.email || email,
            name: userProfile.name,
            role: userProfile.role,
            createdAt: createdAtIso,
            isSuperAdmin: userProfile.isSuperAdmin
          };
          onLogin(user);
        }
      } else {
        // Login existing user with Firebase
        // Early check: verify email exists and supports password sign-in
        const normalizedEmail = email.trim().toLowerCase();
        // Attempt sign-in directly; if it fails, we'll surface a friendly error below.
        const firebaseUser = await signInUser(normalizedEmail, password);
        const userProfile = await getUserProfile(firebaseUser.uid);
        
        if (userProfile) {
          const createdAtIso = (userProfile?.createdAt && typeof (userProfile.createdAt as any).toDate === 'function')
            ? (userProfile.createdAt as any).toDate().toISOString()
            : (typeof userProfile.createdAt === 'string' ? userProfile.createdAt : new Date().toISOString());
          const user: User = {
            id: firebaseUser.uid,
            email: firebaseUser.email || email,
            name: userProfile.name,
            role: userProfile.role,
            createdAt: createdAtIso,
            isSuperAdmin: userProfile.isSuperAdmin
          };
          onLogin(user);
        }
      }
    } catch (err: any) {
      const msg = err?.message || String(err);
      const code = err?.code || '';
      setError(getFriendlyErrorMessage(`${code} ${msg}`));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setResetSent(false);
    try {
      if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        setError('Enter your email above, then click "Forgot password"');
        return;
      }
      await resetPassword(email);
      setResetSent(true);
    } catch (err: any) {
      setError(getFriendlyErrorMessage(err.message));
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginCard}>
                 <div style={styles.logoContainer}>
           <img 
             src="/raedar_logo.png"
             alt="rAEdar Logo" 
             style={styles.logo} 
           />
         </div>

        

        <form onSubmit={handleSubmit} style={styles.form}>
          {isRegistering && (
            <>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={styles.input}
                  placeholder="Enter your name"
                  required
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label style={styles.label}>Invitation Code</label>
                <input
                  type="text"
                  value={invitationCode}
                  onChange={(e) => {
                    setInvitationCode(e.target.value);
                    if (e.target.value.trim()) {
                      validateInvitationCode(e.target.value);
                    } else {
                      setInvitationValid(false);
                      setError('');
                    }
                  }}
                  style={{
                    ...styles.input,
                    borderColor: invitationCode ? (invitationValid ? '#4CAF50' : '#f44336') : '#ddd'
                  }}
                  placeholder="Enter your invitation code"
                  required
                />
                {validatingInvitation && (
                  <p style={{ ...styles.error, color: '#2196F3', marginTop: '5px' }}>
                    Validating invitation code...
                  </p>
                )}
                {invitationCode && !validatingInvitation && (
                  <p style={{ 
                    ...styles.error, 
                    color: invitationValid ? '#4CAF50' : '#f44336',
                    marginTop: '5px' 
                  }}>
                    {invitationValid ? '✅ Valid invitation code' : '❌ Invalid invitation code'}
                  </p>
                )}
              </div>
            </>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="Enter your email"
              required
            />
            
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <div style={styles.passwordContainer}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.passwordInput}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>

          {isRegistering && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirm Password</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={styles.passwordInput}
                  placeholder="Confirm your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.eyeButton}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>
          )}

          {error && <p style={styles.error}>{error}</p>}
          {resetSent && (
            <p style={{ color: '#059669', fontSize: '14px', margin: 0 }}>
              Password reset email sent. Check your inbox.
            </p>
          )}

          <button 
            type="submit" 
            style={{
              ...styles.submitButton,
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : (isRegistering ? 'Create Account' : 'Sign In')}
          </button>

          <button
            type="button"
            onClick={() => setIsRegistering(!isRegistering)}
            style={styles.switchButton}
          >
            {isRegistering ? 'Already have an account? Sign In' : 'Need an account? Register'}
          </button>

          {!isRegistering && (
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{ ...styles.switchButton, marginTop: '-8px' }}
            >
              Forgot password?
            </button>
          )}
        </form>

        
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  loginCard: {
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    padding: '40px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    maxWidth: '400px',
    width: '100%',
  },
  logoContainer: {
    textAlign: 'center' as const,
    marginBottom: '32px',
  },
  logo: {
    height: '120px',
    width: 'auto',
    marginBottom: '24px',
  },

  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'border-color 0.2s',
  },
  passwordContainer: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    padding: '12px 16px',
    paddingRight: '50px', // Make room for eye button
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  eyeButton: {
    position: 'absolute' as const,
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.6,
    transition: 'opacity 0.2s',
    zIndex: 1,
  },
  error: {
    color: '#ef4444',
    fontSize: '14px',
    margin: 0,
  },
  submitButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    border: 'none',
    padding: '14px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
  switchButton: {
    backgroundColor: 'transparent',
    color: '#667eea',
    border: 'none',
    padding: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    textDecoration: 'underline',
  },

};

export default Login;
